// src/services/api.js
import axios from 'axios';
import { extractSoftPaywall, dispatchSoftPaywall } from '../utils/softPaywall';

// Constants
const TOKEN_KEY = 'token';
const RAILWAY_URL = 'https://soldikeeper-backend-production.up.railway.app/api';

// Log the environment settings for debugging
console.log('API Service Init:', {
  nodeEnv: process.env.NODE_ENV,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL
});

// Determine the correct base URL based on the environment
const getBaseUrl = () => {
  // TEMPORARY: Always use Railway URL until environment variables are properly configured
  console.log('✅ Using Railway URL (hardcoded):', RAILWAY_URL);
  return RAILWAY_URL;
  
  /* Original logic - uncomment when env vars are working
  const configuredUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL;
  
  console.log('Configured URL from env:', configuredUrl);
  console.log('Railway fallback URL:', RAILWAY_URL);
  
  if (configuredUrl && configuredUrl.trim() !== '') {
    const apiUrl = configuredUrl.startsWith('http') ? configuredUrl : `https://${configuredUrl}`;
    console.log('✅ Using configured API URL:', apiUrl);
    return apiUrl;
  }
  
  console.log('✅ Using Railway URL:', RAILWAY_URL);
  return RAILWAY_URL;
  */
};

// Create an axios instance with default config
const api = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 15000 // Increased timeout for better mobile support
});

// Track API connectivity status
let isOnline = true;
const setApiStatus = (status) => {
  if (isOnline !== status) {
    isOnline = status;
    // Dispatch event for application-wide status monitoring
    window.dispatchEvent(new CustomEvent('api:status', { 
      detail: { isOnline, timestamp: new Date() } 
    }));
  }
};

// Add interceptor to attach the auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Also include token as x-auth-token for backward compatibility
      config.headers['x-auth-token'] = token;
    }
    
    // Add device identifier for tracking which device made changes
    const deviceId = localStorage.getItem('deviceId');
    if (deviceId) {
      config.headers['x-device-id'] = deviceId;
    }
    
    // Add request logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Request: ${config.method.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    // Mark API as online on successful response
    setApiStatus(true);
    
    // Add response logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Response: ${response.status} ${response.config.method.toUpperCase()} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    // 401 / 403 are expected auth-failure responses with their own handling
    // below — do not log them as unexpected errors to avoid console noise.
    if (error.response?.status !== 401 && error.response?.status !== 403) {
      console.error('API Error:', error);
    }
    
    if (error.message === 'Network Error') {
      console.error('Network error - API endpoint might be unreachable');
      console.log('Current baseURL:', api.defaults.baseURL);
      
      // Mark API as offline
      setApiStatus(false);
      
      // Create a more helpful error message for users
      error.userMessage = "Can't connect to the server. Please check your internet connection.";
    }
    
    if (error.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      // Trigger an application-wide auth error event
      const authErrorEvent = new CustomEvent('auth:error', { 
        detail: { message: 'Your session has expired. Please log in again.' } 
      });
      window.dispatchEvent(authErrorEvent);
    }


    // 402 Soft paywall — never locks the account; surface modal via SoftPaywallContext.
    if (error.response?.status === 402) {
      const soft = extractSoftPaywall(error);
      if (soft) {
        dispatchSoftPaywall(soft);
        error.softPaywall = soft;
        error.code = 'SOFT_PAYWALL';
      }
    }

    // 403 with `upgradeRequired` → tier-locked endpoint. Dispatch a global
    // event so UpgradeModalContext can surface the upgrade modal. The modal
    // applies its own per-session cooldown to avoid spamming the user.
    if (
      error.response?.status === 403 &&
      error.response?.data?.upgradeRequired
    ) {
      window.dispatchEvent(new CustomEvent('upgrade-required', {
        detail: {
          requiredPlan: error.response.data.upgradeRequired,
          feature: error.response.data.feature,
          message: error.response.data.message,
        }
      }));
    }

    return Promise.reject(error);
  }
);

// Standardized error handling for API methods
const handleApiError = (error, context) => {
  console.error(`${context} API error:`, error);
  
  // Format error message for UI display
  const errorMessage = error.userMessage || 
                       error.response?.data?.message || 
                       error.message || 
                       `An error occurred during ${context}`;
  
  // Create a structured error object
  const formattedError = new Error(errorMessage);
  formattedError.context = context;
  formattedError.originalError = error;
  formattedError.status = error.response?.status;
  formattedError.isOffline = !isOnline;
  
  throw formattedError;
};

// Auth related API methods
export const authApi = {
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      if (response.data.data?.token) {
        localStorage.setItem(TOKEN_KEY, response.data.data.token);
      }
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Login');
    }
  },
  
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      // Store token on registration success
      if (response.data.data?.token) {
        localStorage.setItem(TOKEN_KEY, response.data.data.token);
      }
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Register');
    }
  },
  
  logout: async () => {
    try {
      const response = await api.post('/auth/logout');
      localStorage.removeItem(TOKEN_KEY);
      return response.data;
    } catch (error) {
      localStorage.removeItem(TOKEN_KEY); // Remove token even if API fails
      return handleApiError(error, 'Logout');
    }
  },
  
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Get Current User');
    }
  },
  
  refreshToken: async () => {
    try {
      const response = await api.post('/auth/refresh');
      if (response.data.data?.token) {
        localStorage.setItem(TOKEN_KEY, response.data.data.token);
      }
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Refresh Token');
    }
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem(TOKEN_KEY);
  }
};

// Budget related API methods
export const budgetApi = {
  getAll: async () => {
    try {
      const response = await api.get('/budgets');
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Get Budgets');
    }
  },
  
  getById: async (id) => {
    try {
      const response = await api.get(`/budgets/${id}`);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Get Budget');
    }
  },
  
  create: async (budgetData) => {
    try {
      const response = await api.post('/budgets', budgetData);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Create Budget');
    }
  },
  
  update: async (id, budgetData) => {
    try {
      const response = await api.put(`/budgets/${id}`, budgetData);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Update Budget');
    }
  },
  
  delete: async (id) => {
    try {
      const response = await api.delete(`/budgets/${id}`);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Delete Budget');
    }
  }
};

// Transaction related API methods
export const transactionApi = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/transactions', { params });
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Get Transactions');
    }
  },
  
  getById: async (id) => {
    try {
      const response = await api.get(`/transactions/${id}`);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Get Transaction');
    }
  },
  
  create: async (transactionData) => {
    try {
      const response = await api.post('/transactions', transactionData);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Create Transaction');
    }
  },
  
  update: async (id, transactionData) => {
    try {
      const response = await api.put(`/transactions/${id}`, transactionData);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Update Transaction');
    }
  },
  
  delete: async (id) => {
    try {
      const response = await api.delete(`/transactions/${id}`);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Delete Transaction');
    }
  },
  
  getByCategory: async (categoryId) => {
    try {
      const response = await api.get(`/transactions/category/${categoryId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Get Transactions by Category');
    }
  },
  
  getByDateRange: async (startDate, endDate) => {
    try {
      const response = await api.get('/transactions/range', {
        params: { startDate, endDate }
      });
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Get Transactions by Date Range');
    }
  }
};

// User preferences API methods - Fixed path to match backend
export const preferencesApi = {
  get: async () => {
    try {
      const response = await api.get('/user-preferences');
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Get User Preferences');
    }
  },
  
  update: async (preferencesData) => {
    try {
      const response = await api.put('/user-preferences', preferencesData);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Update User Preferences');
    }
  },
  
  getCategories: async () => {
    try {
      const response = await api.get('/user-preferences/categories');
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Get Categories');
    }
  },
  
  saveCategory: async (categoryData) => {
    try {
      let response;
      if (categoryData.id && /^[0-9a-fA-F]{24}$/.test(categoryData.id.toString())) {
        // Update existing category
        response = await api.put(`/user-preferences/categories/${categoryData.id}`, categoryData);
      } else {
        // Create new category
        response = await api.post('/user-preferences/categories', categoryData);
      }
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Save Category');
    }
  },
  
  deleteCategory: async (categoryId) => {
    try {
      const response = await api.delete(`/user-preferences/categories/${categoryId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Delete Category');
    }
  },
  
  updateCategoryBudget: async (categoryId, budget) => {
    try {
      const response = await api.patch(`/user-preferences/categories/${categoryId}/budget-only`, {
        budget: Number(budget)
      });
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Update Category Budget');
    }
  }
};

// Helper functions for API status
export const apiStatus = {
  isOnline: () => isOnline,
  
  checkConnection: async () => {
    try {
      await api.get('/health');
      setApiStatus(true);
      return true;
    } catch (error) {
      setApiStatus(false);
      return false;
    }
  },
  
  onStatusChange: (callback) => {
    const handler = (event) => callback(event.detail);
    window.addEventListener('api:status', handler);
    return () => window.removeEventListener('api:status', handler);
  }
};

// Voice transaction parsing
export const voiceApi = {
  parseTransaction: async (text, locale) => {
    try {
      const response = await api.post('/voice/parse-transaction', { text, locale });
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Voice Parse Transaction');
    }
  },
  parseCommand: async (text, locale) => {
    try {
      const response = await api.post('/voice/command', { text, locale });
      return response.data;
    } catch (error) {
      return handleApiError(error, 'Voice Parse Command');
    }
  },
  transcribe: async (blob, locale) => {
    const fd = new FormData();
    const name = (blob.type || '').includes('mp4') ? 'clip.m4a' : 'clip.webm';
    fd.append('audio', blob, name);
    fd.append('locale', locale || 'en');
    const token = localStorage.getItem(TOKEN_KEY);
    const res = await fetch(`${api.defaults.baseURL}/voice/transcribe`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}`, 'x-auth-token': token } : {},
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.error || data.message || 'Transcription failed');
      err.status = res.status;
      throw err;
    }
    return data;
  },
};

// Export the base API for custom calls
export default api;